import AgentProjectPicker from './AgentProjectPicker';
import { type AgentPeriod, sharedProjectId } from './agentTracking.model';
import { hasDisplayableProject } from './projectSuggestionChoices';
import { useSessionProjectSuggestions } from './SessionProjectSuggestions';

export default function AgentSessionProject({
  periods,
  title,
  onAssign,
  onAssignPeriod,
}: {
  periods: AgentPeriod[];
  title: string;
  onAssign: (id: number | null, segmentIds?: number[]) => Promise<void>;
  onAssignPeriod: (period: AgentPeriod, id: number | null) => Promise<void>;
}) {
  const review = useSessionProjectSuggestions();
  const unassigned = periods.filter(p => p.projectId === null);
  const predicted = unassigned.filter(p => {
    const suggestion = review?.suggestions[p.segmentId];
    return suggestion && hasDisplayableProject(suggestion);
  });
  const projectIds = new Set(predicted.map(p => review?.suggestions[p.segmentId].project_id));
  const predictedIds = new Set(predicted.map(p => p.segmentId));
  const consistentSuggestion =
    predicted.length > 0 &&
    unassigned.every(
      p =>
        predictedIds.has(p.segmentId) ||
        ['waiting_summary', 'queued', 'running'].includes(p.projectPredictionState ?? '')
    ) &&
    projectIds.size === 1;
  const suggestedId = consistentSuggestion
    ? (review?.suggestions[predicted[0].segmentId].project_id ?? undefined)
    : undefined;
  const assignedIds = new Set(periods.flatMap(p => (p.projectId === null ? [] : [p.projectId])));
  const partialAssignment =
    assignedIds.size === 1 && unassigned.length > 0
      ? { projectId: [...assignedIds][0], unassignedCount: unassigned.length }
      : undefined;
  return (
    <AgentProjectPicker
      value={sharedProjectId(periods)}
      suggestedProjectId={assignedIds.size === 0 ? suggestedId : undefined}
      partialAssignment={partialAssignment}
      placeholder={assignedIds.size > 1 ? 'Mixed projects' : 'Assign project'}
      ariaLabel={`Project for ${title}`}
      onChange={onAssign}
      periods={periods}
      onAssignPeriod={onAssignPeriod}
    />
  );
}
